import { useLazyQuery } from '@apollo/client';
import { SingleFormCard } from '@xbcb/display-components';
import { ANNumber } from '@xbcb/form-item-components';
import { CssSize } from '@xbcb/ui-types';
import { Button, Form, message, Space } from 'antd';
import React, { useEffect, useState } from 'react';

import { executeMutation } from 'libs/executeMutation';
import { queryAms } from 'libs/sharedQueries';
import { AmsQueryReferenceType } from '../../__generated__/graphql';

import TeamDatum from 'components/TeamDatum';
import { get } from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { GET_ABI_EXCHANGE_MESSAGES_FOR_AMS } from './queries';
import { StyledCollapse, StyledForm, StyledH2, StyledPanel } from './styles';

const AMS_RESPONSE_APP_CODE = 'C1';

interface CompactQueriesCardProps {
  recordId: string;
  record?: any;
}

const CompactAMSQueriesCard: React.FC<CompactQueriesCardProps> = ({
  recordId,
  record,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [lastRunDate, setLastRunDate] = useState<string | null>(null);
  const [latestC1Message, setLatestC1Message] = useState<any | null>(null);

  const [getAms, { loading, data, networkStatus, error }] = useLazyQuery(
    GET_ABI_EXCHANGE_MESSAGES_FOR_AMS,
    {
      variables: {
        input: {
          tags: {
            values: [
              {
                key: 'usConsumptionEntryId',
                value: recordId,
              },
            ],
          },
        },
        searchPaginationInput: {
          pageNumber: 1,
          pageSize: 100,
        },
      },
      onError: (error) => {
        void message.error('Failed to load AMS results');
      },
      fetchPolicy: 'cache-and-network', // Always fetch fresh data
    },
  );

  useEffect(() => {
    void getAms();
  }, [getAms]);

  const messages: any[] = get(data, 'getAbiExchangeMessages.messages', []);

  const getLatestC1Message = (messages: any[]) => {
    if (!messages || messages.length === 0) {
      return null;
    }

    const c1Messages = messages
      .filter((msg) => msg.json && msg.json.appCode === AMS_RESPONSE_APP_CODE)
      .sort((a, b) => moment(b.time).valueOf() - moment(a.time).valueOf());

    if (c1Messages.length === 0) {
      return null;
    }

    const latestMessage = c1Messages[0];
    const queryRunDate = moment(latestMessage.time).format('YYYY-MM-DD HH:mm');

    return {
      message: latestMessage,
      queryRunDate: queryRunDate,
    };
  };

  useEffect(() => {
    if (messages.length > 0) {
      const latestC1 = getLatestC1Message(messages);
      if (latestC1) {
        setLastRunDate(latestC1.queryRunDate);
        setLatestC1Message(latestC1.message);
      } else {
        setLastRunDate(null);
        setLatestC1Message(null);
      }
    }
  }, [messages]);

  // Extract master bill and house bill numbers from record
  const masterBillNumber = record?.masterBills?.[0]?.number || '';
  const houseBillNumber =
    record?.masterBills?.[0]?.houseBills?.[0]?.number || '';

  const handleAmsQuery = async (value: string, type: AmsQueryReferenceType) => {
    try {
      await form.validateFields([type]);
    } catch (e) {
      void message.error('Please enter a valid value');
      return;
    }

    setIsLoading(true);
    const idempotencyKey = uuidv4();

    try {
      await executeMutation({
        mutation: queryAms,
        variables: {
          idempotencyKey,
          input: {
            amsQueryReferences: [{ type, value }],
          },
        },
        successMessage: 'AMS query transmitted successfully',
      });

      void message.info(
        'AMS query submitted. Results will appear in 60-90 seconds.',
        3,
      );

      setTimeout(() => void getAms(), 60000);
      setTimeout(() => void getAms(), 90000);
    } catch (e) {
      void message.error(
        'Sorry, an error has occurred. Please try again later.',
        5.0,
      );
    }
    setIsLoading(false);
  };

  return (
    <SingleFormCard title="AMS Queries" titlePosition="top" $noMarginBottom>
      <StyledForm form={form}>
        <ANNumber
          label="Master bill of lading"
          form={form}
          fullNamePath={['masterBill']}
          isQueriable
          handleSearch={(value: string) =>
            handleAmsQuery(value, AmsQueryReferenceType.BILL_OF_LADING_NUMBER)
          }
          $itemSize={CssSize.SHORT}
          required
          max={50}
          disabled={isLoading}
          initialValue={masterBillNumber}
        />
        <ANNumber
          label="House bill of lading"
          form={form}
          fullNamePath={['houseBill']}
          isQueriable
          handleSearch={(value: string) =>
            handleAmsQuery(value, AmsQueryReferenceType.BILL_OF_LADING_NUMBER)
          }
          $itemSize={CssSize.SHORT}
          required
          max={50}
          disabled={isLoading}
          initialValue={houseBillNumber}
        />
        <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
          <Button
            onClick={() => void getAms()}
            loading={loading}
            type="default"
            size="small"
          >
            Refresh Results
          </Button>

          <div style={{ fontSize: '12px', color: '#666' }}>
            Status:{' '}
            {loading ? 'Loading...' : `Found ${messages.length} total messages`}
            {error && (
              <div style={{ color: 'red' }}>Error: {error.message}</div>
            )}
            {lastRunDate && (
              <div>
                Last AMS Result:{' '}
                {moment(latestC1Message.time)
                  .local()
                  .format('MMMM D YYYY, h:mm a')}
              </div>
            )}
          </div>
        </Space>
        {latestC1Message &&
          latestC1Message.key &&
          latestC1Message.time &&
          latestC1Message.json && (
            <StyledCollapse bordered={false}>
              <StyledPanel
                key={latestC1Message.time}
                header={
                  <>
                    <StyledH2>{'AMS Query Result'}</StyledH2>
                  </>
                }
              >
                <TeamDatum dataType={'json'} data={latestC1Message.json} />
              </StyledPanel>
            </StyledCollapse>
          )}
      </StyledForm>
    </SingleFormCard>
  );
};

export default CompactAMSQueriesCard;
